import {styled, Icon, theme} from "@reside/ui";
import {Tooltip} from "../tooltip";
import {useState, useEffect, useCallback} from "react";
import {getStonlyRuntimeSnapshot, stonlyDebug} from "./stonlyDebug";

export const StonlyButton = () => {
  const [isWidgetAvailable, setIsWidgetAvailable] = useState(false);

  useEffect(() => {
    stonlyDebug("StonlyButton mount", getStonlyRuntimeSnapshot());

    // Check if the Stonly widget is available
    if (window && (window as any)?.StonlyWidget) {
      stonlyDebug("StonlyButton widget stub detected on mount", {
        ...getStonlyRuntimeSnapshot(),
      });
      setIsWidgetAvailable(true);
      return null;
    }

    stonlyDebug("StonlyButton waiting for widget stub", getStonlyRuntimeSnapshot());

    // Check again after a short delay in case it's still loading
    let pollTimeout: ReturnType<typeof setTimeout>;

    const checkInterval = setInterval(() => {
      if (window && (window as any)?.StonlyWidget) {
        stonlyDebug("StonlyButton widget stub detected after polling", {
          ...getStonlyRuntimeSnapshot(),
        });
        setIsWidgetAvailable(true);
        clearInterval(checkInterval);
        clearTimeout(pollTimeout);
      }
    }, 500);

    // Clear interval after 5 seconds to avoid checking indefinitely
    pollTimeout = setTimeout(() => {
      clearInterval(checkInterval);
      if (!(window as any)?.StonlyWidget) {
        stonlyDebug("StonlyButton polling finished without widget stub", {
          ...getStonlyRuntimeSnapshot(),
        });
      }
    }, 5000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(pollTimeout);
    };
  }, []);

  useEffect(() => {
    if (isWidgetAvailable) {
      stonlyDebug("StonlyButton rendered in header", getStonlyRuntimeSnapshot());
    }
  }, [isWidgetAvailable]);

  const handleHelpButtonClick = useCallback(() => {
    stonlyDebug("Stonly help header button clicked", getStonlyRuntimeSnapshot());
  }, []);

  if (!isWidgetAvailable) return null;

  return (
    <CustomTooltip content="Need Help?">
      <Button
        type="button"
        id="stonly-help-button"
        aria-label="help button - need some help?"
        onClick={handleHelpButtonClick}
      >
        <Icon
          name="help-circled-alt"
          size={25}
          color={theme.color.darkBlue100}
        />
      </Button>
    </CustomTooltip>
  );
};

const CustomTooltip = styled(Tooltip)`
  display: inline-flex;
  align-items: center;
`;

const Button = styled.button`
  border: 0;
  padding: 0;
  background-color: transparent;
  cursor: pointer;
`;
